// src/component/GroupActivity2026Dialog.js
//
// 4期ユーザー向け：2026年グループ活動への参加可否を尋ねるダイアログ。
// 文言・回答期日は systemConfig/groupActivity2026Dialog から渡ってくる props を表示する。
// 既定値は groupActivity2026Defaults.js を参照。
//
// previewMode=true のときは本物のモーダル Dialog を使わず、
// インラインカードとして同じ見た目を描画する（管理画面プレビュー用）。
//
// 開閉：
//  - ✕ ボタン / 背景クリック / Esc で閉じられる。
//  - 未回答のままでも閉じられるが、Launcher 側で「日をまたいだら再表示」する。

import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";

import { AppThemeContext } from "./AppShell";
import { GROUP_ACTIVITY_2026_DEFAULTS } from "./groupActivity2026Defaults";
import { formatDeadlineJa } from "./groupActivity2026Format";

export default function GroupActivity2026Dialog({
  open,
  onClose,
  onRespond,
  chipLabel = GROUP_ACTIVITY_2026_DEFAULTS.chipLabel,
  heading = GROUP_ACTIVITY_2026_DEFAULTS.heading,
  body = GROUP_ACTIVITY_2026_DEFAULTS.body,
  deadline = "",
  // 公開範囲が「管理者のみ」のときだけ、ダイアログ内に「テスト版」注釈を出す
  isAdminOnly = false,
  // 管理画面プレビュー時：モーダルを使わずインラインで描画する
  previewMode = false,
}) {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.92)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [submitting, setSubmitting] = useState(null); // "join" | "decline" | null

  const handleClick = async (response) => {
    if (submitting) return;
    setSubmitting(response);
    try {
      await onRespond(response);
    } finally {
      setSubmitting(null);
    }
  };

  const deadlineInfo = formatDeadlineJa(deadline);

  // -------------------- 共通パーツ --------------------
  const titleNode = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <GroupsRoundedIcon sx={{ fontSize: 22, color: accent, mt: "-1px" }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            letterSpacing: "0.26em",
            fontSize: 11,
            opacity: 0.8,
          }}
        >
          GROUP ACTIVITY 2026
        </Typography>
        <Typography
          component="div"
          sx={{
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          2026年 グループ活動のご案内
        </Typography>
      </Box>
      {!previewMode && onClose && (
        <IconButton
          aria-label="閉じる"
          onClick={() => onClose("closeButton")}
          size="small"
          sx={{ color: textBase }}
        >
          <CloseRoundedIcon />
        </IconButton>
      )}
    </Stack>
  );

  const bodyNode = (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        bgcolor: "rgba(255,255,255,0.96)",
        border: `1px solid ${border}`,
      }}
    >
      {chipLabel && (
        <Chip
          icon={<AutoAwesomeRoundedIcon />}
          label={chipLabel}
          size="small"
          sx={{
            mb: 1.5,
            bgcolor: `${accent}10`,
            color: accent,
            fontWeight: 700,
            letterSpacing: "0.04em",
          }}
        />
      )}

      {heading && (
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, mb: 1, color: textBase }}
        >
          {heading}
        </Typography>
      )}

      {body && (
        <Box
          sx={{
            // 本文だけ固定高さ＋縦スクロール
            maxHeight: { xs: "40vh", sm: 320 },
            overflowY: "auto",
            // スクロールバー余白＆軽い内側パディング
            pr: 1,
            mr: -1,
            // スクロール領域とそれ以外の境界がうっすら見えるようにグラデを暗示
            scrollbarGutter: "stable",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: textBase,
              whiteSpace: "pre-wrap",
            }}
          >
            {body}
          </Typography>
        </Box>
      )}

      {deadlineInfo && (
        <Box
          sx={{
            mt: 2,
            p: 1.2,
            borderRadius: 2,
            bgcolor: deadlineInfo.isPast ? "rgba(220,60,60,0.08)" : `${accent}10`,
            border: `1px solid ${
              deadlineInfo.isPast ? "rgba(220,60,60,0.3)" : `${accent}33`
            }`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EventRoundedIcon
            sx={{
              fontSize: 18,
              color: deadlineInfo.isPast ? "rgb(180,40,40)" : accent,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: deadlineInfo.isPast ? "rgb(160,30,30)" : textBase,
            }}
          >
            回答期日：{deadlineInfo.label}
            {deadlineInfo.isPast && "（期日を過ぎています）"}
          </Typography>
        </Box>
      )}

      {!previewMode && isAdminOnly && (
        <Typography
          variant="body2"
          sx={{ lineHeight: 1.8, color: textBase, opacity: 0.7, mt: 2 }}
        >
          （現在、このダイアログは管理者のみに表示されているテスト版です）
        </Typography>
      )}
    </Box>
  );

  const actionsNode = (
    <Stack
      direction="row"
      justifyContent="flex-end"
      spacing={1}
      sx={{
        borderTop: `1px solid ${border}`,
        bgcolor: "rgba(255,255,255,0.96)",
        px: { xs: 2, sm: 3 },
        py: 1.5,
      }}
    >
      <Button
        onClick={() => handleClick("decline")}
        disabled={!!submitting}
        variant="outlined"
        sx={{
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          px: 2.5,
        }}
      >
        参加しない
      </Button>
      <Button
        onClick={() => handleClick("join")}
        disabled={!!submitting}
        variant="contained"
        sx={{
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 800,
          px: 3,
          bgcolor: accent,
          "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
        }}
      >
        参加する
      </Button>
    </Stack>
  );

  // -------------------- プレビュー（インライン） --------------------
  if (previewMode) {
    return (
      <Box
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          bgcolor: surface,
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            fontWeight: 900,
            letterSpacing: "0.16em",
            fontSize: 14,
            textTransform: "uppercase",
            color: textBase,
            px: 3,
            pt: 2,
            pb: 1,
            borderBottom: `1px solid ${border}`,
            bgcolor: `${accent}07`,
          }}
        >
          {titleNode}
        </Box>
        <Box
          sx={{
            p: 2,
            background:
              "radial-gradient(circle at 0 0, rgba(255,255,255,0.9), transparent 55%)," +
              "radial-gradient(circle at 100% 100%, rgba(255,255,255,0.9), transparent 55%)",
          }}
        >
          {bodyNode}
        </Box>
        {actionsNode}
      </Box>
    );
  }

  // -------------------- 通常表示（モーダル Dialog） --------------------
  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (onClose) onClose(reason);
      }}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          bgcolor: surface,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 900,
          letterSpacing: "0.16em",
          fontSize: 14,
          textTransform: "uppercase",
          color: textBase,
          pb: 1,
          borderBottom: `1px solid ${border}`,
          bgcolor: `${accent}07`,
        }}
      >
        {titleNode}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background:
            "radial-gradient(circle at 0 0, rgba(255,255,255,0.9), transparent 55%)," +
            "radial-gradient(circle at 100% 100%, rgba(255,255,255,0.9), transparent 55%)",
        }}
      >
        {bodyNode}
      </DialogContent>

      <DialogActions
        sx={{
          p: 0,
        }}
      >
        {actionsNode}
      </DialogActions>
    </Dialog>
  );
}
